// src/services/scanLifecycle.service.ts - Scan lifecycle management service

import { customerApi } from './customerApi';
import type { 
  ScanLifecycleManager,
  CreateScanDto,
  ScanCreationResponse,
  Scan,
  ScanVersion,
  CreateScanVersionDto,
  CreateScanMetadataDto,
  ScanMetadata,
  UpdateScanMetadataDto
} from '../types/scan.types';
import { ScanStatus } from '../types/scan.types';
import type { Scene } from '../types/core.types';

/**
 * Service for managing scan lifecycle operations
 * Provides a clean interface for scan creation, status transitions, and metadata management
 */
class ScanLifecycleService implements ScanLifecycleManager {
  
  /**
   * Create a scan with initial status
   */
  async createScanWithStatus(data: CreateScanDto, status: ScanStatus): Promise<ScanCreationResponse> {
    try {
      console.log(`[ScanLifecycle] Creating scan with status: ${status}`);
      
      const scanData = {
        ...data,
        status,
        isFromMobileApp: false // Always false for web app
      };
      
      const result = await customerApi.createScan(scanData);
      
      console.log(`[ScanLifecycle] Scan created successfully: ${result.scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to create scan:`, error);
      throw error;
    }
  }

  /**
   * Update scan status with optional sub-status
   */
  async updateScanStatus(scanId: string, status: ScanStatus, subStatus?: string): Promise<Scan> {
    try {
      console.log(`[ScanLifecycle] Updating scan ${scanId} status to: ${status}${subStatus ? ` (${subStatus})` : ''}`);
      
      const result = await customerApi.updateScanStatus(scanId, status, subStatus);
      
      console.log(`[ScanLifecycle] Scan status updated successfully: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to update scan status:`, error);
      throw error;
    }
  }

  /**
   * Transition scan to uploaded status
   */
  async transitionToUploaded(scanId: string, s3FolderPath: string, inputImageCount: number, sequenceUuid: string): Promise<Scan> {
    try {
      console.log(`[ScanLifecycle] Transitioning scan ${scanId} to uploaded status`);
      
      const result = await customerApi.transitionToUploaded(scanId, s3FolderPath, inputImageCount, sequenceUuid);
      
      console.log(`[ScanLifecycle] Scan transitioned to uploaded: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to transition scan to uploaded:`, error);
      throw error;
    }
  }

  /**
   * Transition scan to processing status
   */
  async transitionToProcessing(scanId: string): Promise<Scan> {
    try {
      console.log(`[ScanLifecycle] Transitioning scan ${scanId} to processing status`);
      
      const result = await customerApi.transitionToProcessing(scanId);
      
      console.log(`[ScanLifecycle] Scan transitioned to processing: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to transition scan to processing:`, error);
      throw error;
    }
  }

  /**
   * Transition scan to completed status
   */
  async transitionToCompleted(scanId: string, reconstructionFolder?: string): Promise<Scan> {
    try {
      console.log(`[ScanLifecycle] Transitioning scan ${scanId} to completed status`);
      
      const result = await customerApi.transitionToCompleted(scanId, reconstructionFolder);
      
      console.log(`[ScanLifecycle] Scan transitioned to completed: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to transition scan to completed:`, error);
      throw error;
    }
  }

  /**
   * Transition scan to failed status
   */
  async transitionToFailed(scanId: string, errorMessage?: string): Promise<Scan> {
    try {
      console.log(`[ScanLifecycle] Transitioning scan ${scanId} to failed status`);
      
      const result = await customerApi.transitionToFailed(scanId, errorMessage);
      
      console.log(`[ScanLifecycle] Scan transitioned to failed: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to transition scan to failed:`, error);
      throw error;
    }
  }

  /**
   * Create scan metadata
   */
  async createScanMetadata(scanId: string, metadata: Record<string, any>): Promise<ScanMetadata> {
    try {
      console.log(`[ScanLifecycle] Creating metadata for scan: ${scanId}`);
      
      const metadataData: CreateScanMetadataDto = {
        scanId,
        metadata
      };
      
      const result = await customerApi.createScanMetadata(metadataData);
      
      console.log(`[ScanLifecycle] Scan metadata created successfully: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to create scan metadata:`, error);
      throw error;
    }
  }

  /**
   * Update scan metadata
   */
  async updateScanMetadata(scanId: string, metadata: Record<string, any>): Promise<ScanMetadata> {
    try {
      console.log(`[ScanLifecycle] Updating metadata for scan: ${scanId}`);

      // First, fetch existing metadata to obtain the document ID (_id)
      const existingMetadata = await customerApi.getScanMetadata(scanId);

      if (!existingMetadata || !(existingMetadata as any)._id) {
        throw new Error('Scan metadata document not found for this scan');
      }

      const metadataId = (existingMetadata as any)._id as string;

      const metadataData: UpdateScanMetadataDto = {
        metadata
      };

      const result = await customerApi.updateScanMetadata(metadataId, metadataData);

      console.log(`[ScanLifecycle] Scan metadata updated successfully: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to update scan metadata:`, error);
      throw error;
    }
  }

  // Scan Version Management
  
  /**
   * Create a new version of a scan
   */
  async createScanVersion(scanId: string, versionData: Omit<CreateScanVersionDto, 'scanId'>): Promise<ScanVersion> {
    try {
      console.log(`[ScanLifecycle] Creating new version for scan: ${scanId}`);
      
      const result = await customerApi.createScanVersion(scanId, {
        ...versionData,
        scanId,
        isFromMobileApp: false // Always false for web app
      });
      
      console.log(`[ScanLifecycle] Scan version created successfully: ${result.versionId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to create scan version:`, error);
      throw error;
    }
  }

  /**
   * Get all versions of a scan
   */
  async getScanVersions(scanId: string): Promise<ScanVersion[]> {
    try {
      console.log(`[ScanLifecycle] Getting versions for scan: ${scanId}`);
      
      const result = await customerApi.getScanVersions(scanId);
      
      console.log(`[ScanLifecycle] Retrieved ${result.length} versions for scan: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to get scan versions:`, error);
      throw error;
    }
  }

  /**
   * Get a specific scan version
   */
  async getScanVersion(versionId: string): Promise<ScanVersion> {
    try {
      console.log(`[ScanLifecycle] Getting scan version: ${versionId}`);
      
      const result = await customerApi.getScanVersion(versionId);
      
      console.log(`[ScanLifecycle] Retrieved scan version: ${versionId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to get scan version:`, error);
      throw error;
    }
  }

  /**
   * Set the active version of a scan
   */
  async setActiveScanVersion(scanId: string, versionId: string): Promise<Scan> {
    try {
      console.log(`[ScanLifecycle] Setting active version for scan ${scanId} to: ${versionId}`);
      
      const result = await customerApi.setActiveScanVersion(scanId, versionId);
      
      console.log(`[ScanLifecycle] Active version set successfully for scan: ${scanId}`);
      return result;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to set active scan version:`, error);
      throw error;
    }
  }

  /**
   * Create a complete scan workflow: create scan -> add metadata 
   * Note: The backend automatically creates the first version when creating a scan
   */
  async createCompleteWebScan(
    title: string,
    scene: Scene,
    sequenceUuid: string,
    additionalMetadata: Record<string, any> = {}
  ): Promise<{ scan: Scan; metadata: ScanMetadata }> {
    try {
      console.log(`[ScanLifecycle] Creating complete web scan: ${title}`);
      
      // Step 1: Create scan with tempsaved status
      const scanData: CreateScanDto = {
        title,
        description: scene.description,
        status: ScanStatus.TempSaved,
        // Use capturePreset as primary category; fall back to legacy scene.type if provided
        category: scene.capturePreset || scene.type,
        isFromMobileApp: false,
        sequenceUuid: sequenceUuid || undefined
      };
      
      const scanResult = await this.createScanWithStatus(scanData, ScanStatus.TempSaved);
      
      // Validate scan result
      if (!scanResult || !scanResult.scanId) {
        throw new Error('Invalid scan creation response: missing scanId');
      }
      
      if (!scanResult.scan) {
        console.warn(`[ScanLifecycle] Scan object not returned, fetching scan by ID: ${scanResult.scanId}`);
        // Fallback: fetch the scan by ID
        try {
          const fetchedScan = await customerApi.getScanById(scanResult.scanId);
          scanResult.scan = fetchedScan;
        } catch (fetchError) {
          throw new Error(`Failed to fetch created scan: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
        }
      }
      
      // Step 2: Create scan metadata
      const metadata: Record<string, any> = {
        capturePreset: scene.capturePreset,
        sceneName: scene.name,
        sceneDescription: scene.description,
        cameraType: scene.cameraType,
        createdFromWebApp: true,
        originalSceneId: scene.id,
        imageCount: scene.images?.length || 0,
        webAppVersion: '1.0.0',
        ...additionalMetadata
      };

      if (scene.location) {
        metadata.location = scene.location;
      }
      
      const metadataResult = await this.createScanMetadata(scanResult.scanId, metadata);
      
      console.log(`[ScanLifecycle] Complete web scan created successfully: ${scanResult.scanId}`);
      if (scanResult.scan.activeVersionId) {
        console.log(`[ScanLifecycle] Backend auto-created version: ${scanResult.scan.activeVersionId}`);
      }
      
      return {
        scan: scanResult.scan,
        metadata: metadataResult
      };
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to create complete web scan:`, error);
      throw error;
    }
  }

  /**
   * Complete upload workflow: transition scan from tempsaved to uploaded
   * Also updates the current version with upload information
   */
  async completeUploadWorkflow(
    scanId: string,
    s3FolderPath: string,
    fileCount: number,
    sequenceUuid: string,
    additionalMetadata: Record<string, any> = {}
  ): Promise<Scan> {
    try {
      console.log(`[ScanLifecycle] Completing upload workflow for scan: ${scanId}`);
      
      // Update scan to uploaded status (this also updates the active version)
      const scan = await this.transitionToUploaded(scanId, s3FolderPath, fileCount, sequenceUuid);
      
      // Update metadata with upload information
      if (Object.keys(additionalMetadata).length > 0) {
        const uploadMetadata = {
          uploadedAt: new Date().toISOString(),
          s3FolderPath,
          sequenceUuid,
          uploadedFileCount: fileCount,
          uploadCompletedFromWebApp: true,
          activeVersionAtUpload: scan.activeVersionId,
          ...additionalMetadata
        };
        
        await this.updateScanMetadata(scanId, uploadMetadata);
      }
      
      console.log(`[ScanLifecycle] Upload workflow completed for scan: ${scanId}`);
      console.log(`[ScanLifecycle] Active version ${scan.activeVersionId} updated with upload info`);
      return scan;
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to complete upload workflow:`, error);
      throw error;
    }
  }

  /**
   * Complete reconstruction workflow: trigger reconstruction and handle status transitions
   * Updates the current version with reconstruction information
   */
  async completeReconstructionWorkflow(
  scanId: string,
  sequenceSetUUID: string,
  sequences: Array<Array<{
    image_key: string;
    timestamp: string;
    image_width_px: number;
    image_height_px: number;
    focal_length_35mm?: number;
  }>>,
  preset?: Scene['capturePreset']
): Promise<void> {
  try {
    const totalImages = sequences.reduce((sum, seq) => sum + seq.length, 0);
    
    console.log(`[ScanLifecycle] 🚀 Starting reconstruction workflow for scan: ${scanId}`, {
      sequenceSetUUID,
      sequenceCount: sequences.length,
      totalImages: totalImages,
      preset,
      imagesPerSequence: sequences.map(seq => seq.length)
    });
    
    // NEW FORMAT: Send sequences to backend
    const reconstructionData = {
      scanId,
      sequence_set_uuid: sequenceSetUUID,  // NEW field name
      sequences: sequences,                 // NEW: Send nested arrays
      options: {},                          // NEW: Options object
      ...(preset ? { preset } : {})
    };
    
    console.log(`[ScanLifecycle] 📤 Sending multi-sequence reconstruction request to backend:`, {
      scanId,
      sequenceSetUUID,
      sequenceCount: sequences.length,
      totalImages: totalImages,
      preset,
      samplePaths: sequences.map((seq, idx) => 
        seq.length > 0 ? `Camera ${idx + 1}: ${seq[0].image_key}` : `Camera ${idx + 1}: empty`
      )
    });
    
    await customerApi.triggerReconstruction(reconstructionData);
    
    console.log(`[ScanLifecycle] ✅ Backend accepted multi-sequence reconstruction request`);
    
    // Update metadata with reconstruction information
    const reconstructionMetadata = {
      reconstructionTriggeredAt: new Date().toISOString(),
      sequenceSetUUID: sequenceSetUUID,      // NEW field name
      reconstructionImageCount: totalImages,
      reconstructionSequenceCount: sequences.length,  // NEW: Track sequences
      triggeredFromWebApp: true,
      ...(preset ? { capturePreset: preset } : {})
    };
    
    await this.updateScanMetadata(scanId, reconstructionMetadata);
    
    console.log(`[ScanLifecycle] ✅ Reconstruction workflow completed successfully`, {
      scanId,
      status: 'queued_for_preprocessing',
      sequenceCount: sequences.length,
      totalImages: totalImages
    });
    console.log(`[ScanLifecycle] 💡 Scan status will be updated to "preprocessing" by backend`);
    console.log(`[ScanLifecycle] 💡 Active version will be updated automatically during processing`);
    
  } catch (error) {
    console.error(`[ScanLifecycle] ❌ Failed to complete reconstruction workflow:`, error);
    
    // If reconstruction fails, update scan status to failed
    try {
      await this.transitionToFailed(
        scanId, 
        `Reconstruction workflow failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      console.log(`[ScanLifecycle] ⚠️ Updated scan status to "failed"`);
    } catch (statusError) {
      console.error(`[ScanLifecycle] Failed to update scan status to failed:`, statusError);
    }
    
    throw error;
  }
}


  /**
   * Create a new version with specific data (for advanced use cases)
   */
  async createNewVersionWithData(
    scanId: string,
    versionData: Omit<CreateScanVersionDto, 'scanId' | 'isFromMobileApp'>,
    setAsActive: boolean = false
  ): Promise<{ version: ScanVersion; scan?: Scan }> {
    try {
      console.log(`[ScanLifecycle] Creating new version with data for scan: ${scanId}`);
      
      // Create the new version
      const version = await this.createScanVersion(scanId, versionData);
      
      let updatedScan: Scan | undefined;
      
      // Set as active if requested
      if (setAsActive) {
        updatedScan = await this.setActiveScanVersion(scanId, version.versionId);
        console.log(`[ScanLifecycle] New version ${version.versionId} set as active for scan: ${scanId}`);
      }
      
      console.log(`[ScanLifecycle] New version created successfully: ${version.versionId}`);
      
      return {
        version,
        scan: updatedScan
      };
    } catch (error) {
      console.error(`[ScanLifecycle] Failed to create new version with data:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const scanLifecycleService = new ScanLifecycleService();
export default scanLifecycleService; 